// Talks to the aulasAI assistant service with the logged-in user's own app token and institute, exactly as the
// rest of the app talks to the backend, so the assistant can never see or do more than the user can.
import { Api } from "services";
import env from "env.json";

// Where the assistant service runs:
//   1. REACT_APP_ASSISTANT_URL, an explicit override at build or start time (handy for local testing).
//   2. ASSISTANT_URL in the environment file (envs/<environment>.json, copied to src/env.json).
// Empty means the assistant is not set up for this build, and the button is not shown at all.
export const ASSISTANT_BASE = (process.env.REACT_APP_ASSISTANT_URL || env.ASSISTANT_URL || "").replace(/\/+$/, "");

const headers = () => ({
  "Content-Type": "application/json",
  Authorization: Api._headers?.Authorization || "",
  orgurl: Api._headers?.orgurl || "",
});

async function failure(res) {
  let detail = res.statusText;
  try {
    const body = await res.json();
    detail = typeof body.detail === "string" ? body.detail : JSON.stringify(body.detail || body);
  } catch (e) {
    // not JSON: keep the status text
  }
  const error = new Error(detail || "Something went wrong");
  error.status = res.status;
  return error;
}

// Reads the server-sent events of a streaming reply: status, delta, then done (or error).
async function stream(path, body, { onStatus, onDelta, signal }) {
  const res = await fetch(`${ASSISTANT_BASE}${path}`, {
    method: "POST", headers: headers(), body: JSON.stringify(body), signal,
  });
  if (!res.ok) throw await failure(res);
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let done = null;
  for (;;) {
    const { value, done: finished } = await reader.read();
    if (finished) break;
    buffer += decoder.decode(value, { stream: true });
    let cut;
    while ((cut = buffer.indexOf("\n\n")) >= 0) {
      const block = buffer.slice(0, cut);
      buffer = buffer.slice(cut + 2);
      const event = (block.match(/^event: (.*)$/m) || [])[1];
      const data = JSON.parse((block.match(/^data: (.*)$/m) || [])[1] || "{}");
      if (event === "status") onStatus && onStatus(data.text);
      else if (event === "delta") onDelta && onDelta(data.text);
      else if (event === "done") done = data;
      else if (event === "error") throw new Error(data.detail || "Something went wrong");
    }
  }
  if (!done) throw new Error("The reply stopped early. Please try again.");
  return done;
}

export const sendMessage = (message, conversationId, handlers) =>
  stream("/v1/chat/stream", { message, conversation_id: conversationId || undefined }, handlers);

export const answerAction = (conversationId, actionId, approve, handlers) =>
  stream(`/v1/chat/${conversationId}/actions/${actionId}/stream`, { approve }, handlers);

async function get(path) {
  const res = await fetch(`${ASSISTANT_BASE}${path}`, { headers: headers() });
  if (res.status === 404) return null;
  if (!res.ok) throw await failure(res);
  return res.json();
}

// The user's latest conversation (within the last hour), to carry on after a reload or a new login.
export const latestConversation = () => get("/v1/chat/latest");

// One of the user's own conversations, to show it again.
export const getConversation = (conversationId) => get(`/v1/chat/${conversationId}`);

// The user's recent chats (the past hour), newest first: [{conversation_id, title, preview, last_active}].
export const recentChats = (limit = 5) => get(`/v1/chat/recent?limit=${limit}`).then((list) => list || []);
